import { ReactNode } from 'react'

import { tv, type VariantProps } from 'tailwind-variants'

import { Icon } from '@/components/atoms'
import { Chip } from '@/components/atoms/Chip/Chip'
import { cn } from '@/utils'

export const alertVariants = tv({
  slots: {
    containerSlot:
      'font-body flex flex-col items-center justify-center gap-2 rounded-lg px-4',
    leftIconSlot: 'flex items-center justify-center',
    rightIconSlot: 'flex items-center justify-center',
    textSlot: 'text-surface-black-100 leading-[150%]',
    closeButtonSlot: 'flex cursor-pointer items-center justify-center p-1'
  },
  variants: {
    variant: {
      information: {
        containerSlot: 'bg-info-bg border-info-stroke border',
        leftIconSlot: 'text-info-icon',
        rightIconSlot: 'text-info-icon',
        closeButtonSlot: 'text-info-icon',
        textSlot: 'text-info-body'
      },
      primary: {
        containerSlot: 'bg-primary-50 border-primary-100 border',
        leftIconSlot: 'text-primary-200',
        rightIconSlot: 'text-primary-200',
        closeButtonSlot: 'text-primary-200',
        textSlot: 'text-info-title'
      },
      success: {
        containerSlot: 'bg-success-bg border-success-stroke border',
        leftIconSlot: 'text-success-icon',
        rightIconSlot: 'text-success-icon',
        closeButtonSlot: 'text-success-icon',
        textSlot: 'text-success-title'
      },
      warning: {
        containerSlot: 'bg-warning-bg border-warning-stroke border',
        leftIconSlot: 'text-warning-icon',
        rightIconSlot: 'text-warning-icon',
        closeButtonSlot: 'text-warning-icon',
        textSlot: 'text-warning-title'
      },
      danger: {
        containerSlot: 'bg-error-bg border-error-stroke border',
        leftIconSlot: 'text-error-icon',
        rightIconSlot: 'text-error-icon',
        closeButtonSlot: 'text-error-icon',
        textSlot: 'text-error-title'
      }
    },
    size: {
      md: {
        containerSlot: 'py-2',
        textSlot: 'text-body-md font-semibold'
      },
      sm: {
        containerSlot: 'py-2',
        textSlot: 'text-caption-lg font-semibold'
      },
      xs: {
        containerSlot: 'py-2',
        textSlot: 'text-caption-sm font-bold'
      }
    }
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md'
  }
})

export type TAlertProps = VariantProps<typeof alertVariants> & {
  message: ReactNode
  onClose?: () => void
  chip?: 'new' | 'summary' | 'reminder' | 'one_off'
  leftIcon?: string
  rightIcon?: string
  variant: 'information' | 'primary' | 'success' | 'warning' | 'danger'
  textAlign?: 'left' | 'center'
  width?: string
}

/**
 * @deprecated Use `Alert`/`AlertTitle`/`AlertDescription` from `@alternative/acqua/molecules` instead. The acqua-ui Alert is being phased out in favor of the acqua design system.
 */
export const Alert = ({
  message,
  onClose,
  size,
  variant,
  chip,
  leftIcon,
  rightIcon,
  textAlign = 'center',
  width = 'w-fit'
}: TAlertProps): JSX.Element => {
  const {
    containerSlot,
    leftIconSlot,
    rightIconSlot,
    textSlot,
    closeButtonSlot
  } = alertVariants({ variant, size })
  const iconSize = size === 'xs' ? 'xs' : size === 'sm' ? 'md' : '2xl'
  const iconColorByVariant: Record<
    NonNullable<TAlertProps['variant']>,
    string
  > = {
    information: 'info-icon',
    primary: 'primary-200',
    success: 'success-icon',
    warning: 'warning-icon',
    danger: 'error-icon'
  }

  return (
    <div className={cn(containerSlot(), width)}>
      <div
        className={cn(
          'flex w-full items-center gap-2',
          textAlign === 'left' ? 'justify-start' : 'justify-center'
        )}
      >
        {chip && (
          <Chip
            variant={variant === 'information' ? 'primary' : variant}
            chip={chip}
            size={size}
          />
        )}
        {leftIcon && (
          <div className={leftIconSlot()}>
            <Icon
              name={leftIcon}
              size={iconSize}
              color={iconColorByVariant[variant]}
              ariaHidden
            />
          </div>
        )}
        <div
          className={cn(
            textSlot(),
            'min-w-0',
            textAlign === 'left' ? 'text-left' : 'text-center'
          )}
        >
          {message}
        </div>
        {rightIcon && (
          <div className={rightIconSlot()}>
            <Icon
              name={rightIcon}
              size={iconSize}
              color={iconColorByVariant[variant]}
              ariaHidden
            />
          </div>
        )}
        {onClose && (
          <button
            onClick={onClose}
            className={cn(
              closeButtonSlot(),
              textAlign === 'left'
                ? 'ml-auto'
                : size === 'xs'
                ? 'ml-12'
                : size === 'sm'
                ? 'ml-12'
                : 'ml-8'
            )}
            aria-label='Close toast'
          >
            <Icon
              name='close'
              size={iconSize}
              color={iconColorByVariant[variant]}
              role='presentation'
              ariaHidden
            />
          </button>
        )}
      </div>
    </div>
  )
}
